// @ts-nocheck

import { map as _map } from 'lodash';
import React, { useState, useCallback, useMemo, useEffect, createRef } from 'react';
import styled, { keyframes, css } from 'styled-components';

import { usePrevious } from '../../hooks';
import { type FlashMessageState } from '../../states/flashmessages';
import theme from '../../styled-components/dark';
import { type FlashMessageSeverity, FlashMessageSeverities } from '../../types/flashmessage';
import { type ToastrType, ToastrTypes } from '../../types/toastr';
import Toastr from '../Toastr';

const delay = 500;
const defaultTimeoutDuration = 3000;

const showKeyframes = keyframes`
  from {
    transform: translateY(0em);
    opacity: 0;
  }

  to {
    transform: translateY(calc(2 * (4.2em + env(safe-area-inset-top))));
    opacity: 0.95;
  }
`;

const hideKeyframes = keyframes`
  from {
    transform: translateY(calc(2 * (4.2em + env(safe-area-inset-top))));
    opacity: 0.95;
  }

  to {
    transform: translateY(0em);
    opacity: 0;
  }
`;

const StyledToastr = styled(Toastr)`
  position: absolute;
  top: calc(0em - (4.2em + env(safe-area-inset-top)));
  left: 1%;
  width: 98%;
  z-index: ${theme.ZIndices.toastr};
  opacity: 0;

  ${({ $showing }) => {
    if (typeof $showing !== 'undefined') {
      if (!$showing) {
        return css`
          animation: ${hideKeyframes} ${delay / 2}ms ease-out forwards;
        `;
      }
      if ($showing) {
        return css`
          animation: ${showKeyframes} ${delay / 2}ms ease-in forwards;
        `;
      }
    }
  }}
`;

export const FlashMessageSeverityByToastrType: { [key: FlashMessageSeverity]: ToastrType } = {
  [FlashMessageSeverities.ERROR]: ToastrTypes.ERROR,
  [FlashMessageSeverities.NOTICE]: ToastrTypes.SUCCESS,
  [FlashMessageSeverities.WARNING]: ToastrTypes.INFO,
};

type PropsType = {
  flashMessages: { [flashMessageId: string]: FlashMessageState };
  delMessage: ({ scope: FlashMessageScope, id: string }) => void;
};

const ToastrFactory = ({ flashMessages, delMessage }: PropsType) => {
  /* La file de toastr */
  const [queue, setQueue] = useState([]);
  /* Les données du toastr en train d'être affiché */
  const currentToastr = useMemo(() => (queue.length ? queue[0] : null), [queue]);
  /* Le toastr précédemment affiché */
  const previousToastr = usePrevious(currentToastr);
  /* Id du setTimeout programmant la prochaine animation de disparition du toastr affiché */
  const [nextDisappearanceAnimPid, setNextDisappearanceAnimPid] = useState(null);
  /* Trigger l'animation du toastr. True => apparition; False => disparition; undefined => pas d'animation */
  const [showing, setShowing] = useState();
  /* Référence du toastr */
  const toastrRef = createRef();

  /* Clear tous les timeout de la file de toastrs avant de la vider */
  const resetQueue = useCallback(() => {
    setQueue((prev) => {
      for (const toastr of prev) {
        const { pid } = toastr;
        if (pid) {
          clearTimeout(pid);
        }
      }
      return [];
    });
  }, []);

  const clearMissingTimeout = useCallback(() => {
    if (nextDisappearanceAnimPid) {
      clearTimeout(nextDisappearanceAnimPid);
    }
  }, [nextDisappearanceAnimPid]);

  useEffect(() => {
    if (flashMessages) {
      // On reset la queue à chaque fois que flashMessages est updated pour avoir une file stable
      resetQueue();
      const scopes = Object.keys(flashMessages);
      for (const currentScope of scopes) {
        setQueue((prev) => [
          ...prev,
          ..._map(flashMessages[currentScope], ({ id, severity, message }) => ({
            id,
            message,
            scope: currentScope,
            type: FlashMessageSeverityByToastrType[severity] ?? ToastrTypes.INFO,
            pid: null, // le pid ne sera set qu'à chaque passage en currentToastr
            onClick: () => {},
          })),
        ]);
      }
    }
  }, [delMessage, flashMessages, resetQueue]);

  useEffect(() => {
    if (currentToastr && !currentToastr.pid && delMessage) {
      // À chaque fois que currentToastr change (<=> queue est updated), on vérifie qu'il n'a pas encore de pid,
      // si c'est bien le cas, on lui set son pid qui prévoie sa future destruction en le supprimant de la queue
      setQueue((prev) => {
        // in the case when the queue is still empty, we return the previous state ( = empty array)
        // ... to avoid error messages like `Cannot read ...property 'scope' of undefined`
        const hasFlashMessages = prev.length;
        if (!hasFlashMessages) {
          return prev;
        }

        const [firstToasterFlashMessage, ...rest] = prev;
        return [
          {
            ...firstToasterFlashMessage,
            pid: setTimeout(() => {
              delMessage({ scope: firstToasterFlashMessage.scope, id: firstToasterFlashMessage.id });
              setQueue((innerPrev) => innerPrev.slice(1));
            }, defaultTimeoutDuration + delay),
          },
          ...rest,
        ];
      });
    }
  }, [currentToastr, delMessage]);

  useEffect(() => {
    // À chaque fois que currentToastr change on anime son apparition et on prévoie sa future animation de disparition
    const { id: currId } = currentToastr ?? {};
    const { id: prevId } = previousToastr ?? {};

    if (currId !== prevId && currentToastr) {
      const toastrNode = toastrRef.current;
      if (toastrNode) {
        setShowing(true);

        setNextDisappearanceAnimPid(
          setTimeout(
            () => {
              setShowing(false);
            },
            defaultTimeoutDuration - delay / 2,
          ),
        );
      }
    }

    return () => {
      clearMissingTimeout();
    };
  }, [currentToastr, previousToastr, toastrRef, clearMissingTimeout]);

  const onToastrClick = useCallback(() => {
    // Au clique du toastr, on le clear son timeout de disparition,
    // on simule sa fonction onClick custom, et on le supprime de la file
    if (currentToastr) {
      const { id, scope, pid, onClick } = currentToastr;
      if (pid) {
        clearTimeout(pid);
      }

      onClick();
      if (nextDisappearanceAnimPid) {
        clearTimeout(nextDisappearanceAnimPid);
        setNextDisappearanceAnimPid(null);
        const toastrNode = toastrRef.current;
        if (toastrNode) {
          setShowing(false);
        }
      }
      if (queue.length) {
        setTimeout(() => {
          setQueue(() => queue.slice(1));
          delMessage({ scope, id });
        }, delay);
      }
    }
  }, [currentToastr, delMessage, nextDisappearanceAnimPid, queue, toastrRef]);

  return (
    <>
      {currentToastr && (
        <StyledToastr
          ref={toastrRef}
          message={currentToastr.message}
          type={currentToastr.type}
          onClick={onToastrClick}
          $showing={showing}
        />
      )}
    </>
  );
};

export default ToastrFactory;
